import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, MapPinned, Plus, Scale, Sprout } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type {
  CommercialDelivery,
  FarmDetail,
  FarmerHarvestSeasonSummary,
  HarvestPlotContext,
  HarvestRegionSeasonSummary,
  HarvestSeason,
} from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const kg = (value: number | undefined) =>
  `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;

async function loadAllDeliveries(): Promise<CommercialDelivery[]> {
  const first = await services.harvest.listPhysicalDeliveries({ page: 1, pageSize: 100 });
  if (first.totalPages <= 1) return first.items;
  const rest = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, index) =>
      services.harvest.listPhysicalDeliveries({ page: index + 2, pageSize: 100 }),
    ),
  );
  return [first, ...rest].flatMap((page) => page.items);
}

async function loadAllSeasonPositions(): Promise<FarmerHarvestSeasonSummary[]> {
  const first = await services.harvest.listSeasonPositions({ page: 1, pageSize: 100 });
  if (first.totalPages <= 1) return first.items;
  const rest = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, index) =>
      services.harvest.listSeasonPositions({ page: index + 2, pageSize: 100 }),
    ),
  );
  return [first, ...rest].flatMap((page) => page.items);
}

export default function HarvestPage() {
  const [seasons, setSeasons] = useState<HarvestSeason[]>();
  const [deliveries, setDeliveries] = useState<CommercialDelivery[]>();
  const [positions, setPositions] = useState<FarmerHarvestSeasonSummary[]>();
  const [regions, setRegions] = useState<HarvestRegionSeasonSummary[]>();
  const [plotContext, setPlotContext] = useState<HarvestPlotContext[]>();
  const [farms, setFarms] = useState<FarmDetail[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [selectedFarmerId, setSelectedFarmerId] = useState("ALL");
  const [loadError, setLoadError] = useState("");
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [showSeasonForm, setShowSeasonForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deliveryForm, setDeliveryForm] = useState({
    farmerId: "",
    farmId: "",
    deliveryDate: new Date().toISOString().slice(0, 10),
    grossQuantity: "",
    acceptedQuantity: "",
    qualityGrade: "GRADE_A",
    moisturePct: "",
    productForm: "DRY_CHERRY",
  });

  const currentYear = new Date().getFullYear();
  const [seasonForm, setSeasonForm] = useState({
    seasonCode: `${currentYear}-S1`,
    seasonName: `${currentYear} Season 1`,
    calendarYear: String(currentYear),
    seasonNumber: "1",
    startDate: `${currentYear}-01-01`,
    endDate: `${currentYear}-05-31`,
    deliveryDeadline: `${currentYear}-05-31`,
    status: "PLANNED" as HarvestSeason["status"],
  });

  const loadPage = async () => {
    setLoadError("");
    try {
      const [seasonRows, deliveryRows, positionRows, regionRows, plotRows, farmRows] = await Promise.all([
        services.harvest.listSeasons(),
        loadAllDeliveries(),
        loadAllSeasonPositions(),
        services.harvest.listRegionSummary(),
        services.harvest.listPlotContext(),
        services.farms.list({ pageSize: 100 }),
      ]);
      setSeasons(seasonRows);
      setDeliveries(deliveryRows);
      setPositions(positionRows);
      setRegions(regionRows);
      setPlotContext(plotRows);
      setFarms(farmRows.items);
      setSelectedSeasonId((current) => {
        if (current && seasonRows.some((season) => season.id === current)) return current;
        return seasonRows.find((season) => season.status === "OPEN")?.id || seasonRows[0]?.id || "";
      });
    } catch (reason: unknown) {
      const message = reason instanceof Error ? reason.message : "Harvest operations could not be loaded.";
      setLoadError(message);
      toast.error(message);
    }
  };

  useEffect(() => { void loadPage(); }, []);

  const selectedSeason = seasons?.find((season) => season.id === selectedSeasonId);
  const seasonDeliveries = useMemo(
    () => (deliveries || []).filter((delivery) => delivery.harvestSeasonId === selectedSeasonId),
    [deliveries, selectedSeasonId],
  );
  const seasonPositions = useMemo(
    () => (positions || []).filter((position) => position.harvestSeasonId === selectedSeasonId),
    [positions, selectedSeasonId],
  );
  const seasonRegions = useMemo(
    () => (regions || []).filter((row) => row.harvestSeasonId === selectedSeasonId),
    [regions, selectedSeasonId],
  );

  const verifiedAccepted = seasonRegions.reduce((sum, row) => sum + row.verifiedAcceptedKg, 0);
  const farmerShare = seasonRegions.reduce((sum, row) => sum + row.farmerShareQuantityKg, 0);
  const companyShare = seasonRegions.reduce((sum, row) => sum + row.companyShareQuantityKg, 0);
  const farmersDelivering = new Set(seasonPositions.map((row) => row.farmerId)).size;

  const farmers = useMemo(() => {
    const map = new Map<string, string>();
    farms.forEach((farm) => map.set(farm.farmerId, farm.farmerName));
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [farms]);

  const farmsForFarmer = farms.filter(
    (farm) => farm.farmerId === deliveryForm.farmerId && farm.farmStatus === "ACTIVE",
  );

  const plotRows = useMemo(() => {
    if (selectedFarmerId === "ALL") return (plotContext || []).slice(0, 60);
    return (plotContext || []).filter((plot) => plot.farmerId === selectedFarmerId);
  }, [plotContext, selectedFarmerId]);

  const selectFarmer = (farmerId: string) => {
    const candidates = farms.filter((farm) => farm.farmerId === farmerId && farm.farmStatus === "ACTIVE");
    setDeliveryForm((current) => ({
      ...current,
      farmerId,
      farmId: candidates.length === 1 ? candidates[0].id : "",
    }));
  };

  const submitDelivery = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedSeason || selectedSeason.status !== "OPEN") {
      toast.error("Select an open harvest season before recording a delivery.");
      return;
    }
    if (!deliveryForm.farmerId || !deliveryForm.farmId || !deliveryForm.deliveryDate || Number(deliveryForm.grossQuantity) <= 0) {
      toast.error("Farmer, Farm, delivery date and gross quantity are required.");
      return;
    }
    setSaving(true);
    try {
      const reference = `HAR-${selectedSeason.seasonCode}-${Date.now().toString().slice(-7)}`;
      await services.harvest.createPhysicalDelivery({
        delivery_reference: reference,
        farmer_id: deliveryForm.farmerId,
        farm_id: deliveryForm.farmId,
        harvest_season_id: selectedSeason.id,
        delivered_at: `${deliveryForm.deliveryDate}T12:00:00.000Z`,
        gross_quantity: Number(deliveryForm.grossQuantity),
        accepted_quantity: deliveryForm.acceptedQuantity ? Number(deliveryForm.acceptedQuantity) : undefined,
        unit: "kg",
        quality_grade: deliveryForm.qualityGrade,
        moisture_pct: deliveryForm.moisturePct ? Number(deliveryForm.moisturePct) : undefined,
        product_form: deliveryForm.productForm,
      });
      toast.success("Harvest delivery recorded", { description: "The delivery is awaiting verification before the 60/40 physical share is locked." });
      setShowDeliveryForm(false);
      setDeliveryForm({
        farmerId: "", farmId: "", deliveryDate: new Date().toISOString().slice(0, 10),
        grossQuantity: "", acceptedQuantity: "", qualityGrade: "GRADE_A",
        moisturePct: "", productForm: "DRY_CHERRY",
      });
      await loadPage();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Delivery could not be recorded.");
    } finally {
      setSaving(false);
    }
  };

  const verifyDelivery = async (id: string) => {
    try {
      await services.harvest.verifyPhysicalDelivery(id);
      toast.success("Delivery verified", { description: "The farmer 60% and ACZON 40% physical quantities have been snapshotted." });
      await loadPage();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Delivery could not be verified.");
    }
  };

  const submitSeason = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const created = await services.harvest.createSeason({
        season_code: seasonForm.seasonCode.trim(),
        season_name: seasonForm.seasonName.trim(),
        calendar_year: Number(seasonForm.calendarYear),
        season_number: Number(seasonForm.seasonNumber),
        start_date: seasonForm.startDate,
        end_date: seasonForm.endDate,
        delivery_deadline: seasonForm.deliveryDeadline,
        status: seasonForm.status,
      });
      toast.success("Harvest season created");
      setShowSeasonForm(false);
      await loadPage();
      setSelectedSeasonId(created.id);
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Harvest season could not be created.");
    } finally {
      setSaving(false);
    }
  };

  const changeSeasonStatus = async (status: HarvestSeason["status"]) => {
    if (!selectedSeason) return;
    setSaving(true);
    try {
      await services.harvest.updateSeasonStatus(selectedSeason.id, status);
      toast.success(`Harvest season ${status.toLowerCase()}`);
      await loadPage();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Harvest season status could not be changed.");
    } finally {
      setSaving(false);
    }
  };

  if (loadError && !seasons) return <ErrorState message={loadError} onRetry={loadPage} />;
  if (!seasons || !deliveries || !positions || !regions || !plotContext) return <LoadingState label="Loading harvest operations" />;

  return (
    <div className="space-y-6" data-testid="harvest-page">
      <PageHeader
        eyebrow="Harvest operations"
        title="Harvest Seasons & Deliveries"
        description="Season-controlled physical coffee intake, regional performance and 60/40 quantity tracking. Plot forecasts remain planning context; deliveries are never falsely attributed to a Plot."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowSeasonForm((value) => !value)}>
              <CalendarDays className="mr-2 size-4" />New season
            </Button>
            <Button onClick={() => setShowDeliveryForm((value) => !value)} disabled={!selectedSeason || selectedSeason.status !== "OPEN"}>
              <Plus className="mr-2 size-4" />Record delivery
            </Button>
          </div>
        }
      />

      {loadError && <Card className="border-destructive/30"><CardContent className="p-4 text-sm text-destructive">{loadError}</CardContent></Card>}

      <Card>
        <CardContent className="p-5">
          <div className="grid items-end gap-4 lg:grid-cols-[minmax(260px,1fr)_3fr]">
            <div className="space-y-2">
              <Label>Harvest season</Label>
              <Select value={selectedSeasonId} onValueChange={setSelectedSeasonId}>
                <SelectTrigger data-testid="harvest-season-select"><SelectValue placeholder="Select season" /></SelectTrigger>
                <SelectContent>
                  {seasons.map((season) => (
                    <SelectItem key={season.id} value={season.id}>
                      {season.seasonName} · {season.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedSeason ? (
              <div className="rounded-xl border bg-muted/20 p-4 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{selectedSeason.seasonName}</span>
                  <StatusBadge status={selectedSeason.status} testId="harvest-season-status" />
                  <span className="text-muted-foreground">Harvest window {selectedSeason.startDate} → {selectedSeason.endDate}</span>
                  <span className="text-muted-foreground">Delivery deadline {selectedSeason.deliveryDeadline}</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Deliveries remain flexible within the season window, but new coffee cannot be posted into a closed season. Locked seasons are frozen from further delivery changes.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedSeason.status === "PLANNED" && (
                    <Button size="sm" onClick={() => void changeSeasonStatus("OPEN")} disabled={saving}>Open season</Button>
                  )}
                  {selectedSeason.status === "OPEN" && (
                    <Button size="sm" variant="outline" onClick={() => void changeSeasonStatus("CLOSED")} disabled={saving}>Close intake</Button>
                  )}
                  {selectedSeason.status === "CLOSED" && (
                    <Button size="sm" onClick={() => void changeSeasonStatus("LOCKED")} disabled={saving}>Lock season</Button>
                  )}
                  {selectedSeason.status === "LOCKED" && (
                    <span className="text-xs font-medium text-muted-foreground">Season locked — delivery records are frozen.</span>
                  )}
                </div>
              </div>
            ) : <p className="text-sm text-muted-foreground">Create a harvest season to begin controlled intake.</p>}
          </div>
        </CardContent>
      </Card>

      {showSeasonForm && (
        <Card data-testid="harvest-season-create-panel">
          <CardHeader><CardTitle className="text-base">Create harvest season</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submitSeason} className="grid gap-4 md:grid-cols-4">
              <div className="space-y-2"><Label>Season code</Label><Input value={seasonForm.seasonCode} onChange={(e: ChangeEvent<HTMLInputElement>) => setSeasonForm((v) => ({ ...v, seasonCode: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Season name</Label><Input value={seasonForm.seasonName} onChange={(e: ChangeEvent<HTMLInputElement>) => setSeasonForm((v) => ({ ...v, seasonName: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Calendar year</Label><Input type="number" value={seasonForm.calendarYear} onChange={(e: ChangeEvent<HTMLInputElement>) => setSeasonForm((v) => ({ ...v, calendarYear: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Season number</Label><Input type="number" min="1" max="9" value={seasonForm.seasonNumber} onChange={(e: ChangeEvent<HTMLInputElement>) => setSeasonForm((v) => ({ ...v, seasonNumber: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Start</Label><Input type="date" value={seasonForm.startDate} onChange={(e: ChangeEvent<HTMLInputElement>) => setSeasonForm((v) => ({ ...v, startDate: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Harvest end</Label><Input type="date" value={seasonForm.endDate} onChange={(e: ChangeEvent<HTMLInputElement>) => setSeasonForm((v) => ({ ...v, endDate: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Delivery deadline</Label><Input type="date" value={seasonForm.deliveryDeadline} onChange={(e: ChangeEvent<HTMLInputElement>) => setSeasonForm((v) => ({ ...v, deliveryDeadline: e.target.value }))} /></div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={seasonForm.status} onValueChange={(value: HarvestSeason["status"]) => setSeasonForm((v) => ({ ...v, status: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="PLANNED">Planned</SelectItem><SelectItem value="OPEN">Open</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="md:col-span-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowSeasonForm(false)}>Cancel</Button>
                <Button type="submit" disabled={saving}>Create season</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {showDeliveryForm && selectedSeason?.status === "OPEN" && (
        <Card data-testid="harvest-delivery-create-panel">
          <CardHeader>
            <CardTitle className="text-base">Record physical coffee delivery</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submitDelivery} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Farmer</Label>
                  <Select value={deliveryForm.farmerId} onValueChange={selectFarmer}>
                    <SelectTrigger><SelectValue placeholder="Select farmer" /></SelectTrigger>
                    <SelectContent>{farmers.map((farmer) => <SelectItem key={farmer.id} value={farmer.id}>{farmer.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Farm</Label>
                  <Select value={deliveryForm.farmId} onValueChange={(farmId: string) => setDeliveryForm((v) => ({ ...v, farmId }))} disabled={!deliveryForm.farmerId}>
                    <SelectTrigger><SelectValue placeholder="Select Farm" /></SelectTrigger>
                    <SelectContent>{farmsForFarmer.map((farm) => <SelectItem key={farm.id} value={farm.id}>{farm.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Delivery date</Label><Input type="date" value={deliveryForm.deliveryDate} onChange={(e: ChangeEvent<HTMLInputElement>) => setDeliveryForm((v) => ({ ...v, deliveryDate: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Gross quantity (kg)</Label><Input type="number" min="0.01" step="0.01" value={deliveryForm.grossQuantity} onChange={(e: ChangeEvent<HTMLInputElement>) => setDeliveryForm((v) => ({ ...v, grossQuantity: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Accepted quantity (kg)</Label><Input type="number" min="0" step="0.01" placeholder="Defaults to gross" value={deliveryForm.acceptedQuantity} onChange={(e: ChangeEvent<HTMLInputElement>) => setDeliveryForm((v) => ({ ...v, acceptedQuantity: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Quality</Label><Input value={deliveryForm.qualityGrade} onChange={(e: ChangeEvent<HTMLInputElement>) => setDeliveryForm((v) => ({ ...v, qualityGrade: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Moisture %</Label><Input type="number" min="0" step="0.1" value={deliveryForm.moisturePct} onChange={(e: ChangeEvent<HTMLInputElement>) => setDeliveryForm((v) => ({ ...v, moisturePct: e.target.value }))} /></div>
                <div className="space-y-2">
                  <Label>Product form</Label>
                  <Select value={deliveryForm.productForm} onValueChange={(productForm: string) => setDeliveryForm((v) => ({ ...v, productForm }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="DRY_CHERRY">Dry cherry</SelectItem><SelectItem value="KIBOKO">Kiboko</SelectItem><SelectItem value="GREEN_BEAN">Green bean</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                No Plot is selected here. A physical delivery may combine coffee from several Plots; Plot-level production remains an expected-production and contract-lifecycle context unless origin is genuinely known.
              </div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setShowDeliveryForm(false)}>Cancel</Button><Button type="submit" disabled={saving}>Record delivery</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Verified accepted</p><p className="mt-1 text-2xl font-semibold">{kg(verifiedAccepted)}</p><Scale className="mt-3 size-5 text-muted-foreground" /></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Farmer share (60%)</p><p className="mt-1 text-2xl font-semibold">{kg(farmerShare)}</p><Sprout className="mt-3 size-5 text-muted-foreground" /></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">ACZON share (40%)</p><p className="mt-1 text-2xl font-semibold">{kg(companyShare)}</p><CheckCircle2 className="mt-3 size-5 text-muted-foreground" /></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Farmers delivering</p><p className="mt-1 text-2xl font-semibold">{farmersDelivering.toLocaleString()}</p><MapPinned className="mt-3 size-5 text-muted-foreground" /></CardContent></Card>
      </div>

      <Tabs defaultValue="deliveries" className="space-y-4">
        <TabsList className="flex h-auto flex-wrap">
          <TabsTrigger value="deliveries">Deliveries</TabsTrigger>
          <TabsTrigger value="farmers">Farmer season positions</TabsTrigger>
          <TabsTrigger value="regions">Regional performance</TabsTrigger>
          <TabsTrigger value="plots">Plot production context</TabsTrigger>
        </TabsList>

        <TabsContent value="deliveries">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Farmer / Farm</TableHead><TableHead>Region</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Accepted</TableHead><TableHead className="text-right">Farmer 60%</TableHead><TableHead className="text-right">ACZON 40%</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {seasonDeliveries.length === 0 ? <TableRow><TableCell colSpan={9} className="py-8 text-center text-muted-foreground">No deliveries recorded for this season.</TableCell></TableRow> :
                  seasonDeliveries.map((delivery) => (
                    <TableRow key={delivery.id}>
                      <TableCell className="font-mono text-xs">{delivery.deliveryReference}</TableCell>
                      <TableCell><span className="font-medium">{delivery.farmerName}</span><p className="text-xs text-muted-foreground">{delivery.farmName || "Farm"}</p></TableCell>
                      <TableCell>{delivery.districtName || "Unassigned"}</TableCell>
                      <TableCell>{new Date(delivery.deliveredAt).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">{kg(delivery.acceptedQuantity ?? delivery.grossQuantity)}</TableCell>
                      <TableCell className="text-right">{delivery.status === "VERIFIED" ? kg(delivery.farmerShareQuantityKg) : "Pending verification"}</TableCell>
                      <TableCell className="text-right">{delivery.status === "VERIFIED" ? kg(delivery.companyShareQuantityKg) : "Pending verification"}</TableCell>
                      <TableCell><StatusBadge status={delivery.status} testId={`harvest-delivery-status-${delivery.id}`} /></TableCell>
                      <TableCell>{(delivery.status === "RECEIVED" || delivery.status === "DRAFT") ? <Button size="sm" variant="outline" onClick={() => void verifyDelivery(delivery.id)}>Verify</Button> : "—"}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="farmers">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Farmer</TableHead><TableHead>Region</TableHead><TableHead className="text-right">Deliveries</TableHead><TableHead className="text-right">Verified harvest</TableHead><TableHead className="text-right">Farmer 60%</TableHead><TableHead className="text-right">ACZON 40%</TableHead><TableHead>Latest delivery</TableHead></TableRow></TableHeader>
              <TableBody>
                {seasonPositions.length === 0 ? <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No farmer positions yet for this season.</TableCell></TableRow> :
                  seasonPositions.map((row) => (
                    <TableRow key={`${row.harvestSeasonId}-${row.farmerId}`}>
                      <TableCell><span className="font-medium">{row.farmerName}</span><p className="text-xs text-muted-foreground">{row.farmName}</p></TableCell>
                      <TableCell>{row.districtName || "Unassigned"}</TableCell>
                      <TableCell className="text-right">{row.deliveryCount}</TableCell>
                      <TableCell className="text-right">{kg(row.verifiedAcceptedKg)}</TableCell>
                      <TableCell className="text-right">{kg(row.farmerShareQuantityKg)}</TableCell>
                      <TableCell className="text-right">{kg(row.companyShareQuantityKg)}</TableCell>
                      <TableCell>{row.latestDeliveryAt ? new Date(row.latestDeliveryAt).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="regions">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>District</TableHead><TableHead className="text-right">Farmers</TableHead><TableHead className="text-right">Deliveries</TableHead><TableHead className="text-right">Verified accepted</TableHead><TableHead className="text-right">Farmer 60%</TableHead><TableHead className="text-right">ACZON 40%</TableHead></TableRow></TableHeader>
              <TableBody>
                {seasonRegions.length === 0 ? <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No regional harvest data for this season.</TableCell></TableRow> :
                  seasonRegions.map((row) => (
                    <TableRow key={`${row.harvestSeasonId}-${row.districtName}`}>
                      <TableCell className="font-medium">{row.districtName}</TableCell><TableCell className="text-right">{row.farmersDelivering}</TableCell><TableCell className="text-right">{row.deliveryCount}</TableCell><TableCell className="text-right">{kg(row.verifiedAcceptedKg)}</TableCell><TableCell className="text-right">{kg(row.farmerShareQuantityKg)}</TableCell><TableCell className="text-right">{kg(row.companyShareQuantityKg)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="plots" className="space-y-4">
          <Card><CardContent className="p-4">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div><p className="font-medium">Plot production & contract context</p><p className="mt-1 max-w-3xl text-sm text-muted-foreground">Each Plot keeps its own 9-year clock and expected yield. This is planning context only; the farmer’s physical deliveries above remain combined unless origin is genuinely known.</p></div>
              <div className="min-w-64 space-y-2"><Label>Filter by farmer</Label><Select value={selectedFarmerId} onValueChange={setSelectedFarmerId}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">All farmers</SelectItem>{farmers.map((farmer) => <SelectItem key={farmer.id} value={farmer.id}>{farmer.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Plot</TableHead><TableHead>9-year term</TableHead><TableHead>Programme year</TableHead><TableHead>Years remaining</TableHead><TableHead className="text-right">Area</TableHead><TableHead className="text-right">{currentYear} expected</TableHead><TableHead className="text-right">{currentYear + 1} expected</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {plotRows.map((plot) => (
                  <TableRow key={plot.plotId}>
                    <TableCell className="font-medium">{plot.plotName}</TableCell>
                    <TableCell>{plot.effectiveDate && plot.expiryDate ? <><span>{plot.effectiveDate}</span><p className="text-xs text-muted-foreground">to {plot.expiryDate}</p></> : "Not enrolled"}</TableCell>
                    <TableCell>{plot.currentProgrammeYear ?? "—"}</TableCell>
                    <TableCell>{plot.approximateYearsRemaining ?? "—"}</TableCell>
                    <TableCell className="text-right">{plot.gpsAreaAcres ?? plot.reportedAreaAcres ?? "—"} ac</TableCell>
                    <TableCell className="text-right">{plot.currentYearExpectedYieldKg == null ? "—" : kg(plot.currentYearExpectedYieldKg)}</TableCell>
                    <TableCell className="text-right">{plot.nextYearExpectedYieldKg == null ? "—" : kg(plot.nextYearExpectedYieldKg)}</TableCell>
                    <TableCell><Badge variant="outline">{plot.programmeTermStatus || plot.plotStatus}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
